import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { Hicon } from './Hicon'
import { AiInsightPanel } from './AiInsightPanel'
import { PropostaIaCard } from './PropostaIaCard'
import { fallbackPorDominio, pageDomain, tituloDoPainel } from './panelInsightsDomains'
import { usePanelInsights } from '../state/PanelInsightsContext'

type Props = { onClose: () => void }

const VERDADE_PADRAO =
  'Verdade = números da tela (KPIs e tabelas). Proposta = leitura da IA — revise antes de agir.'

/**
 * Gaveta «Insights da IA» — abre pelo botão Insights do cabeçalho e mostra a
 * leitura e as sugestões da IA para o painel aberto no momento.
 *
 * Fonte da leitura, por ordem:
 *  1. a tela registrou a própria leitura (`useRegisterPanelInsights`) → mostra ela;
 *  2. senão, o motor por domínio (`/insights/resumo` + reformulação LLM), o mesmo do chat.
 */
export function PanelInsightsDrawer({ onClose }: Props) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { registro, focoDe, setFoco } = usePanelInsights()
  const dominio = pageDomain(pathname)
  const painel = registro?.titulo || tituloDoPainel(pathname)
  const focoSalvo = focoDe(pathname)
  const [focoRascunho, setFocoRascunho] = useState(focoSalvo)
  const aceitaFoco = registro ? registro.aceitaFoco !== false : true

  useEffect(() => {
    setFocoRascunho(focoSalvo)
  }, [focoSalvo, pathname])

  const focoMudou = focoRascunho.trim() !== focoSalvo
  const aplicarFoco = () => {
    setFoco(pathname, focoRascunho)
    if (registro?.recarregar) void registro.recarregar()
  }

  const perguntarFiestaIa = () => {
    window.dispatchEvent(
      new CustomEvent('fiesta:open-rica', {
        detail: {
          prompt: `Explique os insights da IA do painel ${painel} e diga o que devo priorizar${
            focoSalvo ? ` (foco: ${focoSalvo})` : ''
          }.`,
        },
      }),
    )
  }

  const temSugestoes = Boolean(registro?.sugestoes?.length)

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        bgcolor: 'background.default',
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1,
          py: 0.75,
          minWidth: 0,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton onClick={onClose} aria-label="fechar insights" size="small">
          <Hicon name="chevron-left" sx={{ fontSize: 22 }} />
        </IconButton>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          <Hicon name="graph" sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}
            noWrap
          >
            Insights da IA
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {painel}
          </Typography>
        </Box>
        {registro?.recarregar ? (
          <Button
            size="small"
            variant="text"
            onClick={() => void registro.recarregar?.()}
            disabled={Boolean(registro.estado?.loading)}
            startIcon={<Hicon name="refresh" sx={{ fontSize: 18 }} />}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Gerar de novo
          </Button>
        ) : null}
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
        <Stack spacing={2}>
          {registro ? (
            <>
              {registro.estado ? (
                <PropostaIaCard
                  title={`Leitura de ${registro.titulo}`}
                  caption={registro.verdade || VERDADE_PADRAO}
                  state={registro.estado}
                  onRetry={registro.recarregar ? () => void registro.recarregar?.() : undefined}
                />
              ) : null}

              {temSugestoes ? (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography variant="overline" fontWeight={800} letterSpacing={0.08} color="text.secondary">
                      Sugestões
                    </Typography>
                    {!registro.estado ? (
                      <Typography variant="caption" color="text.secondary">
                        {registro.verdade || 'recalculadas a partir do que está em tela'}
                      </Typography>
                    ) : null}
                  </Stack>
                  <Stack spacing={1}>
                    {registro.sugestoes!.map((s) => (
                      <Card
                        key={s.titulo}
                        sx={{
                          borderLeft: '4px solid',
                          borderColor:
                            s.tom === 'critico'
                              ? 'error.main'
                              : s.tom === 'atencao'
                                ? 'warning.main'
                                : 'divider',
                        }}
                      >
                        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                            {s.titulo}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {s.detalhe}
                          </Typography>
                          {s.destino ? (
                            <Link
                              component={RouterLink}
                              to={s.destino}
                              underline="hover"
                              fontWeight={600}
                              sx={{ fontSize: '0.78rem' }}
                              onClick={onClose}
                            >
                              Abrir →
                            </Link>
                          ) : null}
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              ) : null}

              {!registro.estado && !temSugestoes ? (
                <Typography variant="body2" color="text.secondary">
                  Este painel ainda não gerou leitura para o recorte atual.
                </Typography>
              ) : null}
            </>
          ) : dominio ? (
            <Box sx={{ '& > *': { maxWidth: '100% !important' } }}>
              <AiInsightPanel
                dominio={dominio.dominio}
                fallback={fallbackPorDominio(dominio.dominio, painel)}
                foco={focoSalvo || undefined}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Sem leitura da IA para este painel.
            </Typography>
          )}

          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="overline" fontWeight={800} letterSpacing={0.08} color="text.secondary">
                  Foco dos insights
                </Typography>
                {focoSalvo ? (
                  <Chip size="small" label="ativo" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                ) : null}
              </Stack>
              {aceitaFoco ? (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Ex.: priorize margem e ruptura; ignore promoções."
                    value={focoRascunho}
                    onChange={(e) => setFocoRascunho(e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={aplicarFoco}
                      disabled={!focoMudou}
                      sx={{ textTransform: 'none' }}
                    >
                      Aplicar e gerar de novo
                    </Button>
                    {focoSalvo ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setFocoRascunho('')
                          setFoco(pathname, '')
                          if (registro?.recarregar) void registro.recarregar()
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Limpar
                      </Button>
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Guardado neste navegador, por painel. A IA lê o foco junto com os números da
                    tela — continua sem inventar dado.
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  A leitura deste painel vem pronta do serviço da tela; o foco personalizado ainda
                  não se aplica aqui.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            fullWidth
            onClick={perguntarFiestaIa}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Perguntar à Fiesta IA
          </Button>
          <Button
            variant="text"
            onClick={() => {
              onClose()
              navigate('/insights')
            }}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
            endIcon={<Hicon name="chevron-right" sx={{ fontSize: 16 }} />}
          >
            Painel completo
          </Button>
        </Stack>
      </Box>
    </Box>
  )
}
